import React from 'react';

import { ChartLegendProps } from '../../utils/ChartProps';
import { DataKeyType, DefaultDatum } from '../../utils/DataProps';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { DefaultPointType } from '../../utils/XYDataProps';
import {
  ActiveConditionProps,
  ChartColorProps,
  ChartContainerProps,
  ContextLabelProps,
  StatusOrDataProps,
} from '../ChartContainer';
import { XYChart, XYChartBaseProps } from '../XYChart';
import { lineShapeGeneratorFactory } from './lineShapeGeneratorFactory';
import { lineShapeRenderer } from './lineShapeRenderer';
import { lineSegmentTransitionGenerator } from './lineShapeSegmentGenerator';

/**
 * Props that may be passed to a LineChart.
 *
 * @deprecated Use `LineChartProps` within @tanium/ufe instead
 */
export type LineChartProps<
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
> = StatusOrDataProps<
  'data',
  ExtendAndOverride<
    ChartColorProps<TDataKeyType> &
      ChartContainerProps &
      ChartLegendProps &
      ContextLabelProps &
      ActiveConditionProps<TDatum>,
    XYChartBaseProps<TDataKeyType, TPointType, TDatum>
  >
>;

const LineChartInner = <
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
>(
  props: LineChartProps<TDataKeyType, TPointType, TDatum>,
): JSX.Element => {
  return (
    // @ts-expect-error -- FIXME: something about the generics causes TS to think these props are incompatible
    <XYChart
      shapeGeneratorFactory={lineShapeGeneratorFactory}
      shapeRenderer={lineShapeRenderer}
      segmentTransitionGenerator={lineSegmentTransitionGenerator}
      {...props}
    />
  );
};

/**
 * An explicit type declaration for the LineChart component. This is mostly useful to preserve
 * generic type parameters when memoizing the underlying component.
 */
type LineChartComponentType = <
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
>(
  props: LineChartProps<TDataKeyType, TPointType, TDatum>,
) => JSX.Element;

/**
 * A line chart component.
 *
 * @deprecated Use `LineChart` within @tanium/ufe instead
 */
export const LineChart = React.memo(LineChartInner) as LineChartComponentType;
